import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../interfaces';

export interface TransferBlocker {
  code: string;
  message: string;
}

export interface TransferCompanyOption {
  id: number;
  name: string;
  type?: string;
}

export interface DriverHistoryEntry {
  id: number;
  companyId: number;
  companyName: string;
  hiredAt: string;
  dismissedAt: string | null;
  transferReason: string;
  initialRating: number | null;
  notes: string | null;
}

export interface AssignedCar {
  id: number;
  number: string;
  model: string;
  color: string;
}

export interface ValidateTransferResponse {
  canTransfer: boolean;
  blockers: TransferBlocker[];
  warnings: TransferBlocker[];
  assignedCars: AssignedCar[];
  currentCompanyHistory: { hiredAt: string } | null;
}

export interface ValidateUndoTransferResponse {
  canUndo: boolean;
  blockers: TransferBlocker[];
  lastTransfer: {
    historyEntryId: number;
    fromCompanyId: number;
    fromCompanyName: string;
    toCompanyId: number;
    toCompanyName: string;
    transferredAt: string;
  } | null;
}

export interface ValidateUndoDismissResponse {
  canUndoDismiss: boolean;
  blockers: TransferBlocker[];
  lastDismissal: {
    historyEntryId: number;
    companyId: number;
    companyName: string;
    dismissedAt: string;
  } | null;
}

export interface TransferDriverDto {
  targetCompanyId: number;
  hiredAt: string;
  dismissedAt?: string;
  notes?: string;
  carIds?: number[];
  transferBalance?: boolean;
  transferRating?: boolean;
  transferSettings?: boolean;
  settingKeys?: string[];
}

export interface DismissDriverDto {
  dismissedAt?: string;
  notes?: string;
  carIds?: number[];
}

@Injectable({ providedIn: 'root' })
export class DriverTransferService {
  private readonly base = `${environment.apiUrl}/company/executors`;

  constructor(private http: HttpClient) {}

  getAvailableCompanies(excludeCompanyId?: number): Observable<ApiResponse<TransferCompanyOption[]>> {
    const params: Record<string, string> = {};
    if (excludeCompanyId !== undefined) params['excludeCompanyId'] = String(excludeCompanyId);
    return this.http.get<ApiResponse<TransferCompanyOption[]>>(`${this.base}/transfer/companies`, { params });
  }

  getHistory(driverId: number): Observable<ApiResponse<DriverHistoryEntry[]>> {
    return this.http.get<ApiResponse<DriverHistoryEntry[]>>(`${this.base}/${driverId}/transfer/history`);
  }

  validateTransfer(driverId: number, targetCompanyId?: number): Observable<ApiResponse<ValidateTransferResponse>> {
    return this.http.get<ApiResponse<ValidateTransferResponse>>(
      `${this.base}/${driverId}/transfer/validate`,
      { params: targetCompanyId ? { targetCompanyId: String(targetCompanyId) } : {} },
    );
  }

  transfer(driverId: number, dto: TransferDriverDto): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.base}/${driverId}/transfer`, dto);
  }

  dismiss(driverId: number, dto: DismissDriverDto): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.base}/${driverId}/dismiss`, dto);
  }

  validateUndoTransfer(driverId: number): Observable<ApiResponse<ValidateUndoTransferResponse>> {
    return this.http.get<ApiResponse<ValidateUndoTransferResponse>>(`${this.base}/${driverId}/transfer/undo/validate`);
  }

  undoTransfer(driverId: number): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.base}/${driverId}/transfer/undo`, {});
  }

  validateUndoDismiss(driverId: number): Observable<ApiResponse<ValidateUndoDismissResponse>> {
    return this.http.get<ApiResponse<ValidateUndoDismissResponse>>(`${this.base}/${driverId}/dismiss/undo/validate`);
  }

  undoDismiss(driverId: number): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(`${this.base}/${driverId}/dismiss/undo`, {});
  }
}
